import data from "../../shared/sharedData";

export default {
  name: "Appendix D - Foreign Labor Recruiter Information",
  modalPrefill: {
    isVisible: {
      when: "appendixDAttached",
      is: "YES",
    },
    form_id: "9142A",
    type: "foreign-labor",
    machine_name: "foreignLaborRecInfo",
    modal_name: "addProfilesModal",
    title: "Foreign Labor Recruiter Profiles",
    description:
      "Select the Foreign Labor Recruiter profile(s) to populate in the Addendum B table.",
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description:
          "Please specify the whether the applicant is engaging or planning to engage any agent(s) or recruiter(s) in the recruitment of prospective H-2A workers in the Job Opportunity and Supporting Documentation section of this application",
        type: "info",
        isVisible: {
          when: "appendixDAttached",
          is: "untouched",
          operator: "untouched",
        },
      },
      {
        title: "Section Complete",
        description:
          "Because you indicated that the applicant is NOT engaging or planning to engage any agent(s) or recruiter(s) in the recruitment of prospective H-2A workers, this section is not necessary.",
        type: "success",
        isVisible: {
          when: "appendixDAttached",
          is: "N/A",
        },
      },
    ],
  },
  pageInfo: {
    isVisible: {
      when: "appendixDAttached",
      is: "YES",
    },
    text: "Pursuant to 20 CFR 655.137(b), the employer and its attorney or agent (as applicable) must provide to the Department all recruitment-related information required in the Application for Temporary Employment Certification, as defined in 20 CFR 655.103(b), which includes the identity and location of all persons and entities hired by or working for the recruiter or agent, and any of the agents or employees of those persons and entities, to recruit prospective foreign workers for the H-2A job opportunity under this H-2A Application for Temporary Employment Certification, Form ETA-9142A.  This disclosure includes the names of agents and foreign labor recruiters used by the employer, as well as the identities and locations of all persons or entities hired by or working for the primary recruiter in the recruitment of prospective H-2A workers, and the agents or employees of these entities (i.e., persons and entities hired by, or working for, the foreign labor recruiter).  This disclosure is required for all agreements, whether written or verbal, and the required disclosure covers the entirety of the recruitment that brings an H-2A foreign worker to the employer’s certified H-2A job opportunity in the United States.<br/><br/>\
    For each person or entity, complete a section of the Appendix D form by providing identity and location information.  If the employer has more than three (3) persons and entities to identify, the employer must complete as many additional Appendix D forms as are necessary to disclose all persons or entities engaged in foreign worker recruitment for this application.<br/><br/>\
    <b>Important Note:</b> Employers are required to complete Appendix D and provide information about all persons and entities engaged in foreign labor recruiting.  Submission of this information in any other form or format (e.g., a list included in a Foreign Labor Recruitment Agreement) <b>will not be accepted</b> as satisfying this OMB-approved disclosure requirement and will result in the OFLC National Processing Center issuing a NOD that requests a completed Appendix D.  Complete items 1 through 15 with the identity, geographic location, and foreign labor recruiter registration information, as applicable, of each person and entity.  Those items marked with an asterisk (*) are required and must be completed.  Items marked with the section symbol (§) are conditional and are to be completed if applicable.\
    ",
    coloring: "grey",
  },
  sections: [
    {
      isVisible: {
        when: "appendixDAttached",
        is: "YES",
      },
      code: "Appendix D",
      machine_name: "foreignLaborRecInfo",
      isModal: true,
      removeFromFieldArray: true,
      openModalHeader: "Add Foreign Labor Recruiter Information",
      modalHeader: "Add Foreign Labor Recruiter Information",
      sectionHeader: "Foreign Labor Recruiter Information",
      listType: "table",
      validate: ["tableRequired", "tableIncomplete"],
      editButton: true,
      removeLink: "Remove Foreign Labor Recruiter Information",
      modalFields: [
        {
          code: "1",
          tableHeader: "Last Name",
          machine_name: "lastName",
          label: "Last (family) Name",
          widget: "text",
          validate: ["required"],
          maxLength: 120,
          tooltip: `Enter the last (family) name of the person.  If the person has two last names, enter the primary last name first`,
        },
        {
          code: "2",
          tableHeader: "First Name",
          machine_name: "firstName",
          label: "First (given) Name",
          widget: "text",
          validate: ["required"],
          maxLength: 120,
          tooltip: `Enter the first (given) name of the person.`,
        },
        {
          code: "3",
          hideOnTable: true,
          machine_name: "middleName",
          label: "Middle Name(s) §",
          widget: "text",
          maxLength: 120,
          tooltip: `Enter the middle name(s) of the person, if applicable.  Enter “N/A” if the person does not have a middle name or it is not known.`,
        },
        {
          code: "4",
          tableHeader: "Employer/Recruiting Org.",
          machine_name: "recBusinessName",
          label: "Name of Employer/Recruiting Organization",
          widget: "text",
          validate: ["required"],
          maxLength: 120,
          tooltip: `Enter the name of the company or recruiting organization that the person operates or for which the person is employed.  If the person recruits and is directly employed by the employer identified in Section B of the Form ETA-9142A, enter the employer’s legal business name from Field B.1.  If the person recruits directly for the employer and is not directly employed by the employer and does not operate or is not employed by a company or recruiting organization, enter “N/A.”  If the person recruits indirectly for the employer by providing recruiting services through another person or entity, enter the full name of the person or company or recruiting organization for which the person directly provides recruiting services.`,
        },
        {
          code: "5",
          machine_name: "city",
          label: "City",
          widget: "text",
          validate: ["required"],
          maxLength: 120,
          tooltip: `Enter the city in which the person is located.`,
        },
        {
          code: "8",
          machine_name: "country",
          widget: "select",
          label: "Country",
          selection: data.country,
          validate: ["required"],
          isInlineModal: true,
          clearSchema: ["state"],
          tooltip: `Enter the country in which the person is located.`,
        },
        {
          code: "6",
          hideOnTable: true,
          machine_name: "state",
          label: "State/District/Territory",
          widget: "select",
          selection: data.state,
          state: true,
          required: true,
          validate: ["required"],
          isVisible: {
            when: "add_values_field[0]country",
            is: "UNITED STATES OF AMERICA",
          },
          tooltip: `Enter the State, District, or Territory in which the person is located.  If the geographic location does not have a State, District, or Territory designation, enter “N/A.”`,
        },
        {
          code: "7",
          hideOnTable: true,
          machine_name: "postalCode",
          label: "Postal Code",
          widget: "text",
          validate: ["required"],
          maxLength: 120,
          tooltip: `Enter the postal (zip) code in which the person is located.  If the geographic location does not have a postal code designation, enter “N/A.”`,
        },
        {
          code: "9",
          hideOnTable: true,
          machine_name: "province",
          label: "Province §",
          widget: "text",
          maxLength: 120,
          tooltip: `Enter the province in which the person is located, if applicable.  If the geographic location does not have a province designation, enter “N/A.”`,
        },
        {
          code: "10",
          hideOnTable: true,
          machine_name: "registrationNumber1",
          label: "Registration Number 1 §",
          widget: "text",
          validate: [
            {
              isRequired: "registrationNumberCountry1",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: data.country,
                isInlineModal: true,
              },
            },
          ],
          maxLength: 120,
          tooltip: `Enter the unique registration number issued by, or on behalf of, the governmental agency (e.g., Ministry of Labor) with jurisdiction over the country in which the person is authorized or licensed to provide domestic or international recruiting services, if applicable.  The entry in this field must be the most recent and valid registration number issued to the person by the governmental agency.  If the person is providing domestic or international recruiting services in the country that does administer a system for authorizing or licensing foreign labor recruiters <b>and such person is NOT registered or refuses to provide the unique registration number based on due diligence to obtain it by the employer</b>, enter all “9s” in this field. If the person is providing domestic or international recruiting services in a country that does not administer a system for authorizing or licensing any person or entity to conduct domestic or international recruiting services, enter “N/A.”`,
        },
        {
          code: "11",
          hideOnTable: true,
          machine_name: "registrationNumberCountry1",
          widget: "select",
          label: "Issuing Country §",
          selection: data.country,
          prefix: "add_values_field",
          clearSchema: [
            "registrationNumber2",
            "registrationNumberCountry2",
            "registrationNumber3",
            "registrationNumberCountry3",
          ],
          validate: [
            {
              valueChecker: "registrationNumberCountry1",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                check: "ifValueExists",
                valueOf: "registrationNumber1",
                prefix: "add_values_field",
                modal: true,
              },
            },
          ],
          tooltip: `Enter the name of the issuing country (e.g, Mexico) of the unique registration number provided in Question 10, if applicable.  If the person is providing domestic or international recruiting services in the country that does administer a system for authorizing or licensing foreign labor recruiters <b>and such person is NOT registered or refuses to provide the unique registration number based on due diligence to obtain it by the employer</b>, enter “Not Registered” in this field. If the person is providing domestic or international recruiting services in a country that does not administer a system for authorizing or licensing any person or entity to conduct domestic or international recruiting services, enter “N/A.”`,
        },
        {
          code: "12",
          hideOnTable: true,
          machine_name: "registrationNumber2",
          label: "Registration Number 2 §",
          widget: "text",
          maxLength: 120,
          isVisible: {
            when: "add_values_field[0]registrationNumberCountry1",
            is: ["- Select -", "", null, undefined],
            operator: "!==",
          },
          validate: [
            {
              isRequired: "registrationNumberCountry2",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: data.country,
                isInlineModal: true,
              },
            },
          ],
          tooltip: `Enter the second unique registration number issued by, or on behalf of, the governmental agency (e.g., Ministry of Labor) with jurisdiction over the country in which the person is authorized or licensed to provide domestic or international recruiting services, if applicable.  The entry in this field must be the most recent and valid registration number issued to the person by the governmental agency.  If the person is providing domestic or international recruiting services in the country that does administer a system for authorizing or licensing foreign labor recruiters <b>and such person is NOT registered or refuses to provide the unique registration number based on due diligence to obtain it by the employer</b>, enter all “9s” in this field. If the person is providing domestic or international recruiting services in a country that does not administer a system for authorizing or licensing any person or entity to conduct domestic or international recruiting services, enter “N/A.”`,
        },
        {
          code: "13",
          hideOnTable: true,
          machine_name: "registrationNumberCountry2",
          widget: "select",
          label: "Issuing Country §",
          selection: data.country,
          isVisible: {
            when: "add_values_field[0]registrationNumberCountry1",
            is: ["- Select -", "", null, undefined],
            operator: "!==",
          },
          prefix: "add_values_field",
          clearSchema: ["registrationNumber3", "registrationNumberCountry3"],
          validate: [
            {
              valueChecker: "registrationNumberCountry2",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                check: "ifValueExists",
                valueOf: "registrationNumber2",
                prefix: "add_values_field",
                modal: true,
              },
            },
          ],
          tooltip: `Enter the name of the issuing country (e.g, Jamaica) of the unique registration number provided in Question 12, if applicable.  If the person is providing domestic or international recruiting services in the country that does administer a system for authorizing or licensing foreign labor recruiters <b>and such person is NOT registered or refuses to provide the unique registration number based on due diligence to obtain it by the employer</b>, enter “Not Registered” in this field. If the person is providing domestic or international recruiting services in a country that does not administer a system for authorizing or licensing any person or entity to conduct domestic or international recruiting services, enter “N/A.”`,
        },
        {
          code: "14",
          hideOnTable: true,
          machine_name: "registrationNumber3",
          label: "Registration Number 3 §",
          widget: "text",
          maxLength: 120,
          isVisible: {
            when: "add_values_field[0]registrationNumberCountry2",
            is: ["- Select -", "", null, undefined],
            operator: "!==",
          },
          validate: [
            {
              isRequired: "registrationNumberCountry3",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: data.country,
                isInlineModal: true,
              },
            },
          ],
          tooltip: `Enter the third unique registration number issued by, or on behalf of, the governmental agency (e.g., Ministry of Labor) with jurisdiction over the country in which the person is authorized or licensed to provide domestic or international recruiting services, if applicable.  The entry in this field must be the most recent and valid registration number issued to the person by the governmental agency.  If the person is providing domestic or international recruiting services in the country that does administer a system for authorizing or licensing foreign labor recruiters <b>and such person is NOT registered or refuses to provide the unique registration number based on due diligence to obtain it by the employer</b>, enter all “9s” in this field. If the person is providing domestic or international recruiting services in a country that does not administer a system for authorizing or licensing any person or entity to conduct domestic or international recruiting services, enter “N/A.”`,
        },
        {
          code: "15",
          hideOnTable: true,
          machine_name: "registrationNumberCountry3",
          widget: "select",
          label: "Issuing Country §",
          selection: data.country,
          isVisible: {
            when: "add_values_field[0]registrationNumberCountry2",
            is: ["- Select -", "", null, undefined],
            operator: "!==",
          },
          validate: [
            {
              valueChecker: "registrationNumberCountry3",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                check: "ifValueExists",
                valueOf: "registrationNumber3",
                prefix: "add_values_field",
                modal: true,
              },
            },
          ],
          tooltip: `Enter the name of the issuing country (e.g, Mexico) of the unique registration number provided in Question 14, if applicable.  If the person is providing domestic or international recruiting services in the country that does administer a system for authorizing or licensing foreign labor recruiters <b>and such person is NOT registered or refuses to provide the unique registration number based on due diligence to obtain it by the employer</b>, enter “Not Registered” in this field. If the person is providing domestic or international recruiting services in a country that does not administer a system for authorizing or licensing any person or entity to conduct domestic or international recruiting services, enter “N/A.”`,
        },
      ],
    },
  ],
};
